import { Box, Button, Dialog, IconButton, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ConfirmBox from '../../../components/ConfirmBox';
import {
  colorPrimary,
  colorSecondary,
  colorTextBlack
} from '../../../config/theme';
import {
  deleteContentTag,
  getTagList
} from '../../../redux/reducers/contentSlice';
import { handleError, isArrayNotEmpty } from '../../../utils/utils';

const ManageLearningPathsModal = ({ open, handleClose }) => {
  const dispatch = useDispatch();
  const { authData } = useSelector(state => state.auth);
  const {
    tagList,
    isDeleteContentTagLoading,
    isDeleteContentTagSuccess,
    isDeleteContentTagFailed,
    deleteContentTagErrorContainer
  } = useSelector(state => state.content);
  const [tagToDelete, setTagToDelete] = useState(null);

  useEffect(() => {
    if (open) {
      dispatch(getTagList());
    }
  }, [open, dispatch]);

  useEffect(() => {
    if (isDeleteContentTagSuccess) {
      dispatch(getTagList());
      setTagToDelete(null);
    }
  }, [isDeleteContentTagSuccess, dispatch]);

  const handleConfirmDelete = () => {
    if (!tagToDelete) {
      return;
    }

    dispatch(
      deleteContentTag({
        contentTagId: tagToDelete.contentTagId,
        loggedInUserName: authData?.userName
      })
    );
  };

  return (
    <>
      <Dialog
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '8px',
            minWidth: '480px'
          }
        }}
        open={open}
        onClose={handleClose}
      >
        <Box sx={{ padding: '24px' }}>
          <Typography
            fontSize={'20px'}
            color={colorTextBlack}
            fontWeight={'700'}
            mb={1}
          >
            Learning paths / Tags
          </Typography>
          <Typography fontSize={'14px'} color={'text.secondary'} mb={2}>
            Manage existing learning path tags. Deleting a tag removes it from
            the system.
          </Typography>

          <Box sx={{ maxHeight: '320px', overflowY: 'auto' }}>
            {isArrayNotEmpty(tagList) ? (
              tagList.map(tag => (
                <Box
                  key={tag.contentTagId}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 12px',
                    mb: 1,
                    borderRadius: '8px',
                    backgroundColor: colorSecondary
                  }}
                >
                  <Typography fontWeight={600}>{tag.tagName}</Typography>
                  <IconButton
                    aria-label={`Delete ${tag.tagName}`}
                    disabled={isDeleteContentTagLoading}
                    onClick={() => setTagToDelete(tag)}
                    size="small"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))
            ) : (
              <Typography color={'text.secondary'}>No tags found.</Typography>
            )}
          </Box>

          <Box mt={3} display={'flex'} justifyContent={'flex-end'}>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{
                borderColor: colorPrimary,
                borderRadius: '10px',
                color: colorTextBlack,
                borderWidth: '2px',
                height: '42px',
                width: '106px',
                '&:hover': {
                  borderColor: colorPrimary,
                  backgroundColor: 'transparent',
                  borderWidth: '2px'
                }
              }}
            >
              Close
            </Button>
          </Box>

          {isDeleteContentTagFailed && (
            <Typography color="error" fontSize={'14px'} mt={1}>
              {handleError(deleteContentTagErrorContainer?.errorMessage) ||
                handleError(deleteContentTagErrorContainer?.description) ||
                'Unable to delete tag. Please try again.'}
            </Typography>
          )}
        </Box>
      </Dialog>

      <ConfirmBox
        isOpen={Boolean(tagToDelete)}
        title="Delete learning path / tag"
        subtitle={`Are you sure you want to delete "${tagToDelete?.tagName}"?`}
        handleClose={() => setTagToDelete(null)}
        handleSubmit={handleConfirmDelete}
      />
    </>
  );
};

export default ManageLearningPathsModal;
